import { SwiperOptions } from 'swiper/types';

// https://swiperjs.com/swiper-api

interface ISwiperConfigs {
  [key: string]: SwiperOptions;
}

const defaultConfig: SwiperOptions = {
  breakpoints: {
    768: {
      slidesPerView: 1
    }
  }
};

export function getCarouselConfig(context: HTMLElement, configName: string) {
  configurations[configName] = {
    ...configurations[configName],
    navigation: {
      nextEl: `[data-config="${configName}"] .swiper-button-next`,
      prevEl: `[data-config="${configName}"] .swiper-button-prev`,
    },
    pagination: {
      el: context.querySelector<HTMLElement>('.swiper-pagination')
    }
  };

  if (configName === 'swiperComponentCarousel') {
    return buildSwiperConfig(context, { type: 'default' });
  }

  if (configName === 'swiperComponentCarouselPlp') {
    return buildSwiperConfig(context, { type: 'plp' });
  }

  return configurations[configName];
}

/** Shared helper to safely get data attributes **/
const getAttributeValue = (context: HTMLElement, attr: string, fallback: string | number = ''): string | number => {
  return context?.getAttribute(attr) ?? fallback;
};

/** Shared helper to build breakpoint objects dynamically **/
const createBreakpoints = (slides: Record<string, number>, gaps: Record<string, number>) => {
  const breakpoints: Record<number, { slidesPerView?: number; spaceBetween?: number }> = {};

  const addBreakpoint = (size: number, spv?: number, gap?: number) => {
    if (spv || gap) {
      breakpoints[size] = {};
      if (spv) breakpoints[size].slidesPerView = spv;
      if (gap) breakpoints[size].spaceBetween = gap;
    }
  };

  addBreakpoint(768, slides.md, gaps.md);
  addBreakpoint(992, slides.lg, gaps.lg);
  addBreakpoint(1200, slides.xl, gaps.xl);
  addBreakpoint(1600, slides.xxl, gaps.xxl);

  return breakpoints;
};

/** Main configuration builder **/
const buildSwiperConfig = (context: HTMLElement, options: { type: 'default' | 'plp' }): SwiperOptions => {
  // Slides per view per breakpoint
  const slides = {
    sm: +getAttributeValue(context, 'data-sm-slides', 3),
    md: +getAttributeValue(context, 'data-md-slides', 3),
    lg: +getAttributeValue(context, 'data-lg-slides', 4),
    xl: +getAttributeValue(context, 'data-xl-slides', 5),
    xxl: +getAttributeValue(context, 'data-xxl-slides', 5)
  };

  // Gaps per breakpoint
  const gaps = {
    sm: +getAttributeValue(context, 'data-sm-gap', 10),
    md: +getAttributeValue(context, 'data-md-gap', 10),
    lg: +getAttributeValue(context, 'data-lg-gap', 10),
    xl: +getAttributeValue(context, 'data-xl-gap', 10),
    xxl: +getAttributeValue(context, 'data-xxl-gap', 10)
  };

  const loop = getAttributeValue(context, 'data-loop') === 'True';
  const centre = getAttributeValue(context, 'data-centre') === 'True';
  const breakpoints = createBreakpoints(slides, gaps);
  const swiperWrapper = context.querySelector('.swiper-wrapper') as HTMLElement;
  const swiperSlides = context.querySelectorAll('.swiper-slide');
  const slidesPerGroup = 1;

  const maxSlidesPerView = Math.ceil(Math.max(slides.sm, slides.md, slides.lg, slides.xl, slides.xxl));
  const repeatCount = Math.ceil((maxSlidesPerView + slidesPerGroup) / swiperSlides.length);

  /** Special handling for PLP type - CSS variables are set dynamically so slide widths and gaps are handled by CSS. **/
  if (options.type === 'plp') {
    const setVars = () => {
      const w = window.innerWidth;
      let spv = slides.sm;
      let gap = gaps.sm;

      if (w >= 1600) { spv = slides.xxl; gap = gaps.xxl; }
      else if (w >= 1200) { spv = slides.xl; gap = gaps.xl; }
      else if (w >= 992) { spv = slides.lg; gap = gaps.lg; }
      else if (w >= 768) { spv = slides.md; gap = gaps.md; }

      context.style.setProperty('--spv', String(spv));
      context.style.setProperty('--gap', `${gap}px`);
    };

    setVars();
    window.addEventListener('resize', setVars, { passive: true });
  }

  if (loop) swiperWrapper.innerHTML += swiperWrapper.innerHTML.repeat(repeatCount);

  // Shared configuration defaults
  const sharedConfig: SwiperOptions = {
    resizeObserver: true,
    loop,
    loopAddBlankSlides: true,
    spaceBetween: gaps.sm,
    slidesPerGroup: slidesPerGroup,
    centeredSlides: centre,
    navigation: {
      nextEl: context.querySelector<HTMLElement>('.swiper-button-next'),
      prevEl: context.querySelector<HTMLElement>('.swiper-button-prev'),
    },
    breakpoints
  };

  // Type-specific overrides
  if (options.type === 'plp') {
    return {
      ...sharedConfig,
      watchOverflow: true,
      roundLengths: true, // avoid sub-pixel drift
      slidesPerView: 'auto', // key for PLP
      breakpoints: {
        768: { spaceBetween: gaps.md },
        992: { spaceBetween: gaps.lg },
        1200: { spaceBetween: gaps.xl },
        1600: { spaceBetween: gaps.xxl }
      }
    };
  }

  // Default (non-PLP)
  return {
    ...sharedConfig,
    slidesPerView: slides.sm
  };
};

const configurations = {
  productPageCarousel: {
    slidesPerView: 1,
    navigation: {
      nextEl: '[data-config="productPageCarousel"] .swiper-button-next',
      prevEl: '[data-config="productPageCarousel"] .swiper-button-prev'
    },
    hashNavigation: {
      watchState: true
    },
    pagination: {
      el: '[data-config="productPageCarousel"] .swiper-pagination',
      type: 'bullets',
      clickable: true
    },
    spaceBetween: 30,
    loop: true,

    breakpoints: {
      970: {
        slidesPerView: 2,
        spaceBetween: 0,
      }
    }
  },
  productPageCarouselSlideout: {
    slidesPerView: 2,
    navigation: {
      nextEl: '[data-config="productPageCarousel"] .swiper-button-next',
      prevEl: '[data-config="productPageCarousel"] .swiper-button-prev'
    },
    hashNavigation: {
      watchState: true
    },
    pagination: {
      el: '[data-config="productPageCarousel"] .swiper-pagination',
      type: 'bullets',
      clickable: true
    },
    spaceBetween: 0,
    loop: document.querySelectorAll('[data-config="productPageCarouselSlideout"] .swiper-slide').length > 2,

    breakpoints: {
      970: {
        slidesPerView: 2,
        spaceBetween: 0,
      }
    }
  },
  productPageCarouselSingle: {
    slidesPerView: 1,
    navigation: {
      nextEl: '[data-config="productPageCarouselSingle"] .swiper-button-next',
      prevEl: '[data-config="productPageCarouselSingle"] .swiper-button-prev'
    },
    pagination: {
      el: '.swiper-pagination',
      type: 'bullets'
    },
    spaceBetween: 30,
    centerInsufficientSlides: true,
    loop: false,
    breakpoints: {
      1200: {
        slidesPerView: 1
      }
    }
  },
  brandPageReviews: {
    ...defaultConfig,
    navigation: {
      nextEl: '[data-config="brandPageReviews"] .swiper-button-next',
      prevEl: '[data-config="brandPageReviews"] .swiper-button-prev'
    },
    pagination: {
      el: '.swiper-pagination',
      type: 'bullets'
    },
    scrollbar: {
      el: '.swiper-scrollbar',
      draggable: true
    }
  },
  reviewPanel: {
    ...defaultConfig,
    navigation: {
      nextEl: '[data-config="reviewPanel"] .swiper-button-next',
      prevEl: '[data-config="reviewPanel"] .swiper-button-prev'
    },
  },

  brandPageProducts: {
    ...defaultConfig,
    scrollbar: {
      el: '.swiper-scrollbar',
      draggable: true
    },
    spaceBetween: 25
  },

  simpleAssociatedProducts: {
    ...defaultConfig,
    navigation: {
      nextEl: '[data-config="simpleAssociatedProducts"] .swiper-button-next',
      prevEl: '[data-config="simpleAssociatedProducts"] .swiper-button-prev'
    },
    pagination: {
      el: '.swiper-pagination',
      type: 'bullets'
    },
    spaceBetween: 16,
    centerInsufficientSlides: true,
    slidesPerView: 4,
    slidesPerGroup: 1,
    breakpoints: {
      0: {
        slidesPerView: 1
      },
      768: {
        slidesPerView: 2,
        spaceBetween: 15,
        slidesPerGroup: 1
      },
      1200: {
        slidesPerView: 3,
        spaceBetween: 5,
        slidesPerGroup: 1
      }
    }
  },
  productPageSimpleAssociatedProducts: {
    ...defaultConfig,
    navigation: {
      nextEl: '[data-config="productPageSimpleAssociatedProducts"] .swiper-button-next',
      prevEl: '[data-config="productPageSimpleAssociatedProducts"] .swiper-button-prev'
    },
    pagination: {
      el: '.swiper-pagination',
      type: 'bullets'
    },
    spaceBetween: 32,
    centerInsufficientSlides: true,
    slidesPerView: 2,
    slidesPerGroup: 1,
    breakpoints: {
      0: {
        slidesPerView: 1.3
      },
      768: {
        slidesPerView: 3,
        spaceBetween: 5,
        slidesPerGroup: 1
      },
      1200: {
        slidesPerView: 3,
        spaceBetween: 35,
        slidesPerGroup: 1
      }
    }
  },
  cmsPageDynamicAssociatedProducts: {
    ...defaultConfig,
    navigation: {
      nextEl: '[data-config="cmsPageDynamicAssociatedProducts"] .swiper-button-next',
      prevEl: '[data-config="cmsPageDynamicAssociatedProducts"] .swiper-button-prev'
    },
    pagination: {
      el: '.swiper-pagination',
      type: 'bullets'
    },
    spaceBetween: 16,
    centerInsufficientSlides: true,
    slidesPerGroup: 1,
    breakpoints: {
      0: {
        slidesPerView: 1.3
      },
      768: {
        slidesPerView: 3,
        spaceBetween: 16,
        slidesPerGroup: 1
      },
      1200: {
        slidesPerView: 4,
        spaceBetween: 16,
        slidesPerGroup: 1
      }
    }
  },
  carouselVybeProducts: {
    ...defaultConfig,
    navigation: {
      nextEl: '[data-config="carouselVybeProducts"] .swiper-button-next',
      prevEl: '[data-config="carouselVybeProducts"] .swiper-button-prev'
    },
    pagination: {
      el: '.swiper-pagination',
      type: 'bullets'
    },
    spaceBetween: 10,
    centerInsufficientSlides: true,
    slidesPerView: 5,
    slidesPerGroup: 1,
    loop: true,
    breakpoints: {
      0: {
        slidesPerView: 2,
        slidesPerGroup: 1
      },
      768: {
        slidesPerView: 3,
        spaceBetween: 5,
        slidesPerGroup: 1
      },
      1200: {
        slidesPerView: 5,
        spaceBetween: 5,
        slidesPerGroup: 1
      }
    }
  },

  ajax: {
    ...defaultConfig,
    navigation: {
      nextEl: '[data-config="ajax"] .swiper-button-next',
      prevEl: '[data-config="ajax"] .swiper-button-prev'
    },
    pagination: {
      el: '.swiper-pagination',
      type: 'bullets'
    },
    slidesPerView: 1,
    loop: true
  },
  insidePocket: {
    navigation: false,
    pagination: false,
    slidesPerView: 1,
    loop: false,
    breakpoints: {
      0: {
        slidesPerView: 'auto',
        grabCursor: true,
        centeredSlides: false,
        slidesOffsetBefore: 45,
        slidesOffsetAfter: 45,
        rewind: true,
        navigation: {
          nextEl: '.carousel-next-arrow'
        },
        spaceBetween: 24
      },
      768: {
        spaceBetween: 1,
        centeredSlides: false,
      }
    }
  },
  relatedPosts: {
    loop: true,
    spaceBetween: 16,
    slidesPerGroup: 1,
    navigation: {
      nextEl: '[data-config="relatedPosts"] .swiper-button-next',
      prevEl: '[data-config="relatedPosts"] .swiper-button-prev',
    },
    slidesPerView: 1,
    breakpoints: {
      640: {
        slidesPerView: 1,
      },
      768: {
        slidesPerView: 2,
      },
      1024: {
        slidesPerView: 3,
      },
    },
  },
  quickview: {
    ...defaultConfig,
    navigation: {
      nextEl: '[data-config="quickview"] .swiper-button-next',
      prevEl: '[data-config="quickview"] .swiper-button-prev'
    },
    loop: true,
    sliderPerView: 1,
    breakpoints: {
      992: {
        slidesPerView: 2,
        spaceBetween: 5
      },
      768: {
        slidesPerView: 1
      }
    }
  },

  uspslider: {
    loop: true,
    autoplay: {
      disableOnInteraction: false
    },
    sliderPerView: 1
  },

  completeTheOutfit: {
    spaceBetween: 15,
    navigation: {
      nextEl: '[data-config="completeTheOutfit"] .swiper-button-next',
      prevEl: '[data-config="completeTheOutfit"] .swiper-button-prev'
    },
    slidesPerView: 2
  },

  rolexKeepExploring: {
    ...defaultConfig,
    navigation: {
      nextEl: '[data-config="rolexKeepExploring"] .swiper-button-next',
      prevEl: '[data-config="rolexKeepExploring"] .swiper-button-prev'
    },
    pagination: {
      el: '.swiper-pagination',
      type: 'bullets'
    },
    spaceBetween: 15,
    centerInsufficientSlides: true,
    slidesPerView: 3,
    slidesPerGroup: 3,
    breakpoints: {
      768: {
        slidesPerView: 2,
        spaceBetween: 5,
        slidesPerGroup: 2
      }
    }
  },

  testimonialCarousel: {
    spaceBetween: 32,
    navigation: {
      nextEl: '[data-config="testimonialCarousel"] .swiper-button-next',
      prevEl: '[data-config="testimonialCarousel"] .swiper-button-prev'
    },
    pagination: {
      el: '.swiper-pagination',
      type: 'bullets'
    },
    slidesPerView: 3,
    breakpoints: {
      1200: {
        slidesPerView: 2
      },
      768: {
        slidesPerView: 1
      }
    }
  },
  pdpImageThumbnails: {
    spaceBetween: 15,
    direction: 'vertical',
    navigation: {
      nextEl: '[data-config="pdpImageThumbnails"] .swiper-button-next',
      prevEl: '[data-config="pdpImageThumbnails"] .swiper-button-prev'
    },
    autoHeight: true,
    slidesPerView: 7
  },
  loopPdp: {
    slidesPerView: 1,
    spaceBetween: 30,
    loop: true,
    pagination: {
      el: '.swiper-pagination',
      type: 'bullets'
    }
  },

  fiveProducts: {
    ...defaultConfig,
    navigation: {
      nextEl: '[data-config="fiveProducts"] .swiper-button-next',
      prevEl: '[data-config="fiveProducts"] .swiper-button-prev'
    },
    pagination: {
      el: '.swiper-pagination',
      type: 'bullets'
    },
    spaceBetween: 15,
    centerInsufficientSlides: true,
    slidesPerView: 5,
    slidesPerGroup: 5,
    breakpoints: {
      0: {
        slidesPerView: 1,
        slidesPerGroup: 1
      },
      768: {
        slidesPerView: 2,
        slidesPerGroup: 2
      },
      1200: {
        slidesPerView: 5,
        slidesPerGroup: 5
      }
    }
  },

  threeProducts: {
    ...defaultConfig,
    navigation: {
      nextEl: '[data-config="threeProducts"] .swiper-button-next',
      prevEl: '[data-config="threeProducts"] .swiper-button-prev'
    },
    pagination: {
      el: '.swiper-pagination',
      type: 'bullets'
    },
    spaceBetween: 15,
    centerInsufficientSlides: true,
    slidesPerView: 3,
    slidesPerGroup: 3,
    breakpoints: {
      0: {
        slidesPerView: 1,
        slidesPerGroup: 1
      },
      768: {
        slidesPerView: 2,
        slidesPerGroup: 2
      },
      1200: {
        slidesPerView: 3,
        slidesPerGroup: 3
      }
    }
  },
  pdpCarousel: {
    ...defaultConfig,
    navigation: {
      nextEl: '[data-config="pdpCarousel"] .swiper-button-next',
      prevEl: '[data-config="pdpCarousel"] .swiper-button-prev'
    },
    pagination: {
      el: '[data-config="pdpCarousel"] .swiper-pagination',
      type: 'bullets'
    },
    spaceBetween: 32,
    centerInsufficientSlides: true,
    slidesPerView: 3,
    slidesPerGroup: 3,
    breakpoints: {
      0: {
        slidesPerView: 1,
        slidesPerGroup: 1
      },
      768: {
        slidesPerView: 3,
        slidesPerGroup: 3
      },
      1200: {
        slidesPerView: 3,
        slidesPerGroup: 3
      }
    }
  },

    pdpCarouselYmal: {
        ...defaultConfig,
        navigation: {
            nextEl: '[data-config="pdpCarouselYmal"] .swiper-button-next',
            prevEl: '[data-config="pdpCarouselYmal"] .swiper-button-prev'
        },
        pagination: {
            el: '[data-config="pdpCarouselYmal"] .swiper-pagination',
            type: 'bullets'
        },
        spaceBetween: 32,
        centerInsufficientSlides: true,
        slidesPerView: 4,
        slidesPerGroup: 4,
        breakpoints: {
            0: {
                slidesPerView: 2,
                slidesPerGroup: 2
            },
            768: {
                slidesPerView: 3
            },
            950: {
                slidesPerView: 4
            }
        }
    },


  crossSellCarousel: {
    ...defaultConfig,
    navigation: {
      nextEl: '[data-config="crossSellCarousel"] .swiper-button-next',
      prevEl: '[data-config="crossSellCarousel"] .swiper-button-prev'
    },
    pagination: {
      el: '[data-config="crossSellCarousel"] .swiper-pagination',
      type: 'bullets'
    },
    spaceBetween: 20,
    centerInsufficientSlides: false,
    slidesPerView: 2,
    slidesPerGroup: 1,
    watchOverflow: true,
    breakpoints: {
      0: {
        slidesPerView: 2
      },
      768: {
        slidesPerView: 2,
      },
      1200: {
        slidesPerView: 2,
      }
    }
  },

  categoryLinks: {
    slidesPerView: 11,
    spaceBetween: 20,
    freeMode: {
      enabled: true,
      sticky: false,
      momentumBounce: false
    },
    breakpoints: {
      0: {
        slidesPerView: 2
      },
      768: {
        slidesPerView: 6,
      },
      992: {
        slidesPerView: 8
      },
      1200: {
        slidesPerView: 8,
      },
      1600: {
        slidesPerView: 11,
      }
    }
  }
} as ISwiperConfigs;

export default configurations;
